/**
 * Generic utility to automatically switch tabs or click elements
 * This handles cross-browser compatibility and slow network conditions
 * Can be reused for any tab switching or element clicking scenario
 */

interface TabSwitcherOptions {
    targetElementId?: string; // ID of element to click/activate
    targetElementSelector?: string; // CSS selector as alternative to ID
    activeClassName?: string; // Class name that indicates active state
    activeAttribute?: string; // Attribute that indicates active state
    activeAttributeValue?: string; // Value of attribute when active
    deactivateElementId?: string; // Optional: ID of element to deactivate
    containerClassName?: string; // Optional: Class to watch for container
    maxRetries?: number;
    retryDelay?: number;
    debug?: boolean;
}

/**
 * Generic function to switch to a target element/tab
 * @returns boolean indicating if the switch was successful
 */
function switchToTargetElement(config: {
    targetElementId?: string;
    targetElementSelector?: string;
    activeClassName?: string;
    activeAttribute?: string;
    activeAttributeValue?: string;
    debug?: boolean;
}): boolean {
    const {
        targetElementId,
        targetElementSelector,
        activeClassName = 'isActive',
        activeAttribute = 'aria-selected',
        activeAttributeValue = 'true',
        debug = false,
    } = config || {};

    try {
        // Find the target element by ID or selector
        let targetElement: Element | null = null;

        if (targetElementId) {
            targetElement = document.getElementById(targetElementId);
        } else if (targetElementSelector) {
            targetElement = document.querySelector(targetElementSelector);
        }

        if (!targetElement) {
            if (debug)
                console.log(`[TabSwitcher] Target element not found: ${targetElementId || targetElementSelector}`);
            return false;
        }

        // Check if it's already active
        const isAlreadyActive =
            targetElement.classList.contains(activeClassName) ||
            targetElement.getAttribute(activeAttribute) === activeAttributeValue;

        if (isAlreadyActive) {
            if (debug) console.log(`[TabSwitcher] Element already active: ${targetElementId || targetElementSelector}`);
            return true;
        }

        // Simulate a click event that works across all browsers
        const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
        });

        // Dispatch the click event
        targetElement.dispatchEvent(clickEvent);

        // For better compatibility, also try native click if available
        if (targetElement instanceof HTMLElement && typeof targetElement.click === 'function') {
            targetElement.click();
        }

        // Also trigger focus for accessibility
        if (targetElement instanceof HTMLElement) {
            targetElement.focus({ preventScroll: true });
        }

        if (debug) console.log(`[TabSwitcher] Switched to element: ${targetElementId || targetElementSelector}`);
        return true;
    } catch (error) {
        if (debug) console.error('[TabSwitcher] Error switching element:', error);
        return false;
    }
}

/**
 * Generic tab/element switcher with DOM monitoring
 * Uses MutationObserver for efficient DOM monitoring
 */
export function initializeTabSwitcher(options?: TabSwitcherOptions): () => void {
    const {
        targetElementId,
        targetElementSelector,
        activeClassName = 'isActive',
        activeAttribute = 'aria-selected',
        activeAttributeValue = 'true',
        deactivateElementId,
        containerClassName,
        maxRetries = 10, // Changed from 50 to 10
        retryDelay = 200, // Changed from 100 to 200
        debug = false,
    } = options || {};

    let observer: MutationObserver | null = null;
    let retryCount = 0;
    let retryTimeout: NodeJS.Timeout | null = null;

    // Function to attempt the element switch
    const attemptSwitch = () => {
        const success = switchToTargetElement({
            targetElementId,
            targetElementSelector,
            activeClassName,
            activeAttribute,
            activeAttributeValue,
            debug,
        });

        if (success) {
            // Clean up observer if switch was successful
            if (observer) {
                observer.disconnect();
                observer = null;
            }
            if (retryTimeout) {
                clearTimeout(retryTimeout);
                retryTimeout = null;
            }
            return true;
        }

        // Retry if not successful and under retry limit
        if (retryCount < maxRetries) {
            retryCount++;
            if (debug) console.log(`[TabSwitcher] Retry attempt ${retryCount}/${maxRetries}`);
            retryTimeout = setTimeout(attemptSwitch, retryDelay);
        } else {
            if (debug) console.log('[TabSwitcher] Max retries reached');
            cleanup();
        }

        return false;
    };

    // Set up MutationObserver to watch for element injection
    const observerCallback: MutationCallback = (mutations) => {
        // Check if any mutation added the target element
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                const addedNodes = Array.from(mutation.addedNodes);

                // Check if any added node contains the target element
                const hasTargetElements = addedNodes.some((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as Element;

                        // Check for target element by ID or selector
                        if (targetElementId && element.id === targetElementId) {
                            return true;
                        }

                        if (targetElementSelector) {
                            if (element.matches?.(targetElementSelector)) {
                                return true;
                            }
                            if (element.querySelector?.(targetElementSelector)) {
                                return true;
                            }
                        }

                        // Check for container class if provided
                        if (containerClassName && element.classList?.contains(containerClassName)) {
                            return true;
                        }

                        // Also check within the element for the target
                        if (targetElementId && element.querySelector?.(`#${targetElementId}`)) {
                            return true;
                        }
                    }
                    return false;
                });

                if (hasTargetElements) {
                    if (debug) console.log('[TabSwitcher] Target elements detected in DOM');
                    // Small delay to ensure everything is rendered
                    setTimeout(() => attemptSwitch(), 50);
                }
            }

            // Also check for attribute changes on relevant elements
            if (mutation.type === 'attributes' && mutation.target instanceof Element) {
                const target = mutation.target;

                // Check if this is our target element or related element
                const isRelevant =
                    (targetElementId && target.id === targetElementId) ||
                    (deactivateElementId && target.id === deactivateElementId) ||
                    (targetElementSelector && target.matches?.(targetElementSelector)) ||
                    (containerClassName && target.classList?.contains(containerClassName));

                if (isRelevant) {
                    // Element state might have changed, attempt switch
                    setTimeout(() => attemptSwitch(), 50);
                }
            }
        }
    };

    // Start observing
    observer = new MutationObserver(observerCallback);

    // Observe the entire document body for changes
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', activeAttribute, 'aria-controls'],
    });

    // Also attempt initial switch in case element is already loaded
    setTimeout(() => attemptSwitch(), 100);

    // Cleanup function
    const cleanup = () => {
        if (observer) {
            observer.disconnect();
            observer = null;
        }
        if (retryTimeout) {
            clearTimeout(retryTimeout);
            retryTimeout = null;
        }
        if (debug) console.log('[TabSwitcher] Cleanup completed');
    };

    // Return cleanup function
    return cleanup;
}

/**
 * Convenience function for Reviews.io Product Review tab switching
 * Uses the generic switcher with Reviews.io specific configuration
 */
export function initializeReviewTabSwitcher(options?: Partial<TabSwitcherOptions>): () => void {
    return initializeTabSwitcher({
        targetElementId: 'product-review-tab',
        activeClassName: 'isActive',
        activeAttribute: 'aria-selected',
        activeAttributeValue: 'true',
        containerClassName: 'R-TabControls__item',
        maxRetries: 10,
        retryDelay: 200,
        ...options, // Allow overrides
    });
}

/**
 * Simple one-time attempt to switch to a specific element
 * Useful for cases where you know the element is already loaded
 */
export function switchToElementOnce(targetElementId?: string, targetElementSelector?: string): boolean {
    return switchToTargetElement({
        targetElementId,
        targetElementSelector,
        debug: false,
    });
}

/**
 * Legacy compatibility - one-time attempt to switch to Product Review tab
 */
export function switchToProductReviewTabOnce(): boolean {
    return switchToElementOnce('product-review-tab');
}
